import { formatMoney } from 'utils/money-util'
import { match } from 'utils/pattern-match-util'
import ScopeChangeRequest, {
  ResponderType,
  ScopeChangeRequestStatus,
} from '../ScopeChangeRequest'
import { SearchableScopeChangeRequestSortField } from '../ScopeChangeRequestsTableColumn'

/**
 * Formats the phase to a string
 * @param phase
 * @returns
 */
export function formatPhase(phase: ScopeChangeRequestStatus) {
  return match(phase, {
    [ScopeChangeRequestStatus.Accepted]: () => 'Accepted',
    [ScopeChangeRequestStatus.Rejected]: () => 'Rejected',
    [ScopeChangeRequestStatus.ApplyFailed]: () => 'Rejected',
    [ScopeChangeRequestStatus.PendingFinalization]: () => 'Finalizing',
    [ScopeChangeRequestStatus.PendingAdminApproval]: () => 'Pending',
    [ScopeChangeRequestStatus.PendingClientApproval]: () => 'Pending',
    [ScopeChangeRequestStatus.Unspecified]: () => '',
  })
}

/**
 * Formats the status to a string that will be displayed in the table
 * @param scopeChangeRequest
 * @returns
 */
export function formatTableStatus(status: ScopeChangeRequestStatus) {
  return match(status, {
    [ScopeChangeRequestStatus.Accepted]: () => 'Accepted',
    [ScopeChangeRequestStatus.Rejected]: () => 'Rejected',
    [ScopeChangeRequestStatus.ApplyFailed]: () => 'System rejected',
    [ScopeChangeRequestStatus.PendingFinalization]: () => 'Reviewing',
    [ScopeChangeRequestStatus.PendingAdminApproval]: () => 'Reviewing',
    [ScopeChangeRequestStatus.PendingClientApproval]: () => 'Awaiting',
    [ScopeChangeRequestStatus.Unspecified]: () => '',
  })
}

/**
 * Formats the status to a string that will be displayed in the side panel
 * @param scopeChangeRequest
 * @returns
 */
export function formatSidePanelStatus(scopeChangeRequest: ScopeChangeRequest) {
  return match(scopeChangeRequest.status, {
    [ScopeChangeRequestStatus.Accepted]: () =>
      scopeChangeRequest.lastResponder?.responderType === ResponderType.Admin ||
      (scopeChangeRequest.isCreatedByAdmin && scopeChangeRequest.isAccepted)
        ? 'Admin accepted'
        : 'Client accepted',
    [ScopeChangeRequestStatus.Rejected]: () =>
      scopeChangeRequest.lastResponder?.responderType === ResponderType.Admin
        ? 'Admin rejected'
        : 'Client rejected',
    [ScopeChangeRequestStatus.PendingFinalization]: () => 'Reviewing',
    [ScopeChangeRequestStatus.PendingAdminApproval]: () =>
      'Admin approval required',
    [ScopeChangeRequestStatus.PendingClientApproval]: () =>
      'Client approval required',
    [ScopeChangeRequestStatus.ApplyFailed]: () => 'System rejected',
    [ScopeChangeRequestStatus.Unspecified]: () => '',
  })
}

/**
 * Formats the status to a string
 * @param scopeChangeRequest
 * @returns
 */
export function formatStatus(scopeChangeRequest: ScopeChangeRequest) {
  return match(scopeChangeRequest.status, {
    [ScopeChangeRequestStatus.Accepted]: () =>
      scopeChangeRequest.lastResponder?.responderType === ResponderType.Admin
        ? 'Admin accepted'
        : 'Client accepted',
    [ScopeChangeRequestStatus.Rejected]: () =>
      scopeChangeRequest.lastResponder?.responderType === ResponderType.Admin
        ? 'Admin rejected'
        : 'Client rejected',
    [ScopeChangeRequestStatus.PendingFinalization]: () =>
      'Pending finalization',
    [ScopeChangeRequestStatus.PendingAdminApproval]: () =>
      'Pending admin approval',
    [ScopeChangeRequestStatus.PendingClientApproval]: () =>
      'Pending client approval',
    [ScopeChangeRequestStatus.ApplyFailed]: () => 'System rejected',
    [ScopeChangeRequestStatus.Unspecified]: () => '',
  })
}

/**
 * Formats the status to a string
 * @param status
 * @returns
 */
export function formatStatusSingleWord(status: ScopeChangeRequestStatus) {
  return match(status, {
    [ScopeChangeRequestStatus.Accepted]: () => 'accepted',
    [ScopeChangeRequestStatus.Rejected]: () => 'rejected',
    [ScopeChangeRequestStatus.ApplyFailed]: () => 'rejected',
    [ScopeChangeRequestStatus.PendingFinalization]: () => 'pending',
    [ScopeChangeRequestStatus.PendingAdminApproval]: () => 'pending',
    [ScopeChangeRequestStatus.PendingClientApproval]: () => 'pending',
    [ScopeChangeRequestStatus.Unspecified]: () => '',
  })
}

/**
 * Formats the number as USD currency and adds the sign (+,-) with a space before the sign
 * @param value
 * @returns
 */
export function formatSum(value: number) {
  return `${value >= 0 ? '+' : '-'} ${formatMoney(Math.abs(value))}`
}

const defaultDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

/**
 * Formats the date to a string
 * @param date
 * @returns
 */
export function formatDate(date: Date) {
  return defaultDateFormatter.format(date)
}

const signedNumberFormatter = new Intl.NumberFormat('en-US', {
  signDisplay: 'exceptZero',
})

/**
 * Formats the number to a string with its respective sign (+,-)
 * @param quantity
 * @returns
 */
export function formatQuantity(quantity: number) {
  return signedNumberFormatter.format(quantity)
}

export function formatSortField(field: SearchableScopeChangeRequestSortField) {
  return match(field, {
    id: () => 'Id',
    create_time: () => 'Date',
  })
}
